import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const KEY = (g) => `ohc_gate_${g}`;
export const isAdmin = (u) => ["super_admin", "admin"].includes(u?.role);

function SetPasswordDialog({ gate, isSet, open, onOpenChange, onDone }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const save = async () => {
    if (f.new_password !== f.confirm) { toast.error("Passwords don't match"); return; }
    try {
      await api.post(`/gates/${gate}/password`, { current_password: f.current_password, new_password: f.new_password });
      toast.success("Section password saved"); setF({ current_password: "", new_password: "", confirm: "" }); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fields = [...(isSet ? [["current_password", "Current section password"]] : []), ["new_password", "New password (min 6 characters)"], ["confirm", "Confirm new password"]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid={`gate-${gate}-setpw-dialog`}>
        <DialogHeader><DialogTitle>{isSet ? "Change" : "Set"} section password</DialogTitle></DialogHeader>
        {fields.map(([k, l]) => <div key={k}><Label>{l}</Label><Input type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`gate-${gate}-${k}`} /></div>)}
        <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid={`gate-${gate}-setpw-save`}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Admin-only password gate; non-admins pass straight through
export default function PasswordGate({ gate, title, children }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [unlocked, setUnlocked] = useState(() => !admin || !!sessionStorage.getItem(KEY(gate)));
  const [isSet, setIsSet] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [setOpen, setSetOpen] = useState(false);
  const refresh = () => api.get(`/gates/${gate}`).then(({ data }) => setIsSet(data.is_set)).catch(() => {});
  useEffect(() => { if (admin) refresh(); }, [admin]); // eslint-disable-line
  useEffect(() => {
    const h = () => { if (admin) setUnlocked(false); };
    window.addEventListener("ohc-gate-locked", h);
    return () => window.removeEventListener("ohc-gate-locked", h);
  }, [admin]);

  const unlock = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post(`/gates/${gate}/unlock`, { password: pw }); sessionStorage.setItem(KEY(gate), data.token); setPw(""); setUnlocked(true); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); }
  };
  const lock = () => { sessionStorage.removeItem(KEY(gate)); setUnlocked(false); };

  if (unlocked) {
    return (
      <div>
        {admin && (
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={() => setSetOpen(true)} data-testid={`gate-${gate}-change-btn`}><KeyRound className="w-4 h-4 mr-1" /> Change Password</Button>
            <Button variant="outline" size="sm" onClick={lock} data-testid={`gate-${gate}-lock-btn`}><Lock className="w-4 h-4 mr-1" /> Lock</Button>
          </div>
        )}
        {children}
        <SetPasswordDialog gate={gate} isSet open={setOpen} onOpenChange={setSetOpen} />
      </div>
    );
  }
  return (
    <div className="max-w-md mx-auto mt-16 bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center" data-testid={`gate-${gate}`}>
      <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-[#4F46E5] flex items-center justify-center mx-auto"><ShieldCheck className="w-7 h-7" /></div>
      <h2 className="font-heading text-xl font-extrabold text-[#312E81] mt-4">{title} is locked</h2>
      {isSet === false ? (
        <>
          <p className="text-sm text-slate-500 mt-1">Set a section password to protect this page. You'll need it every time you open it.</p>
          <Button onClick={() => setSetOpen(true)} className="mt-5 bg-[#4F46E5] hover:bg-[#4338CA]" data-testid={`gate-${gate}-set-btn`}><KeyRound className="w-4 h-4 mr-1" /> Set Password</Button>
        </>
      ) : (
        <form onSubmit={unlock} className="mt-4 text-left">
          <p className="text-sm text-slate-500 text-center mb-4">Enter the {title} password to continue.</p>
          <Input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder={`Enter ${title} password`} data-testid={`gate-${gate}-password`} />
          {err && <p className="text-xs text-rose-600 mt-1" data-testid={`gate-${gate}-error`}>{err}</p>}
          <Button type="submit" className="w-full mt-3 bg-[#4F46E5] hover:bg-[#4338CA]" data-testid={`gate-${gate}-unlock-btn`}><Lock className="w-4 h-4 mr-1" /> Unlock</Button>
        </form>
      )}
      <SetPasswordDialog gate={gate} isSet={!!isSet} open={setOpen} onOpenChange={setSetOpen} onDone={refresh} />
    </div>
  );
}
